import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors, Settings } from '@/types'

import { toast } from 'sonner'
import { useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useRefresh } from '@/hooks/use-refresh'
import { validateSettings } from '@/lib/validation'
import { formatMoney, getCurrency } from '@/lib/format'
import { ApiError, apiRequest, getErrorMessage } from '@/lib/api'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import FormField, { describedBy } from '@/components/generals/form-field'
import { CURRENCIES, LIMITS } from '@/constants/inventory'

interface BusinessSettingsFormProps {
    settings: Settings
}

const BusinessSettingsForm: FC<BusinessSettingsFormProps> = ({ settings }): ReactNode => {
    const refresh = useRefresh()
    const [businessName, setBusinessName] = useState(settings.businessName)
    const [currency, setCurrency] = useState(settings.currency)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [saving, setSaving] = useState(false)

    const changed = businessName.trim() !== settings.businessName || currency !== settings.currency
    const preview = formatMoney(1234.5, getCurrency(currency))

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const result = validateSettings({ businessName, currency })

        if (!result.ok) {
            setErrors(result.errors)

            return
        }

        setErrors({})
        setSaving(true)

        try {
            await apiRequest('/api/settings', { method: 'PUT', body: result.value })
            toast.success('Ajustes guardados')
            await refresh()
        }
        catch (error) {
            if (error instanceof ApiError && Object.keys(error.fieldErrors).length) setErrors(error.fieldErrors)
            else toast.error(getErrorMessage(error))
        }
        finally {
            setSaving(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-5'>
            <div className='grid gap-4 sm:grid-cols-2'>
                <FormField
                    id='settings-business-name'
                    label='Nombre del negocio'
                    hint='Aparece en el menú y en el panel.'
                    error={errors.businessName}
                >
                    <Input
                        id='settings-business-name'
                        value={businessName}
                        maxLength={LIMITS.businessName}
                        autoComplete='organization'
                        onChange={event => {
                            setBusinessName(event.target.value)
                            setErrors(previous => ({ ...previous, businessName: undefined }))
                        }}
                        aria-invalid={Boolean(errors.businessName) || undefined}
                        aria-describedby={describedBy('settings-business-name', errors.businessName, 'hint')}
                    />
                </FormField>

                <FormField
                    id='settings-currency'
                    label='Moneda'
                    hint={`Así se verán los importes: ${preview}`}
                    error={errors.currency}
                >
                    <NativeSelect
                        id='settings-currency'
                        value={currency}
                        onChange={event => setCurrency(event.target.value)}
                        aria-describedby={describedBy('settings-currency', errors.currency, 'hint')}
                        className='w-full'
                    >
                        {CURRENCIES.map(option => (
                            <NativeSelectOption key={option.code} value={option.code}>
                                {option.label} ({option.symbol})
                            </NativeSelectOption>
                        ))}
                    </NativeSelect>
                </FormField>
            </div>

            <div className='flex flex-wrap items-center justify-end gap-2 border-t pt-4'>
                {changed && (
                    <Button
                        type='button'
                        variant='ghost'
                        onClick={() => {
                            setBusinessName(settings.businessName)
                            setCurrency(settings.currency)
                            setErrors({})
                        }}
                    >
                        Descartar
                    </Button>
                )}

                <Button type='submit' disabled={!changed || saving}>
                    {saving && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            </div>
        </form>
    )
}

export default BusinessSettingsForm
